/**
 * 登录回跳 URL 相关工具：构造 ?from= 与校验回跳目标。
 *
 * 流程：
 * 1. 401 触发整页跳转登录页时 (AuthContext) 用 buildLoginRedirectUrl()
 *    把当前 path/search/hash 编码后写入 `?from=`。
 * 2. 登录页 (Clerk <SignIn forceRedirectUrl> / AuthGuard 在 AUTHENTICATED+/login 时)
 *    用 getSafePostLoginPath() 从 ?from= 取值并做开放重定向防护。
 */

/**
 * 构造跳转登录页的 URL，把当前 location 作为 ?from= 编码进去。
 *
 * 当前已在 /login、/register 时不再写新的 from（当前 URL 不是用户原本想去的页面），
 * 但会**保留已有的 ?from=**：避免登录页上后台请求触发的二次 401
 * 把之前 401 写入的 ?from= 抹掉。
 */
export function buildLoginRedirectUrl(): string {
  const { pathname, search, hash } = window.location
  const isAuthPath = pathname === '/login' || pathname === '/register'
  if (isAuthPath) {
    const existingFrom = new URLSearchParams(search).get('from')
    return existingFrom
      ? `/login?from=${encodeURIComponent(existingFrom)}`
      : '/login'
  }
  const from = pathname + search + hash
  return `/login?from=${encodeURIComponent(from)}`
}

/** react-router 的 `Location`（以及 `location.state.from`）里我们关心的部分。 */
export interface PathLike {
  pathname?: string
  search?: string
  hash?: string
}

/**
 * 把 location-like 对象拼成完整站内路径 `pathname + search + hash`。
 *
 * ⚠️ 只取 `pathname` 会把 query / hash 丢掉——`/intents?intentId=123` 登录后
 * 变成 `/intents`，深链指向的那条 issue 打不开，用户以为链接坏了。
 */
export function toFullPath(loc?: PathLike | null): string | null {
  if (!loc?.pathname) return null
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

/**
 * AuthGuard 路由内拦截未登录用户时，构造带 `?from=` 的登录页 URL。
 *
 * 为什么必须写进 query 而不是只放 `<Navigate state>`：Clerk 登录会发生**整页跳转**
 * （OAuth 回跳、Clerk 内部导航），router state 是内存态，跳转后就没了，
 * 回跳只能退化成 /dashboard。写进 URL 才能活过整页跳转。
 *
 * 目标同样过 getSafePostLoginPath —— 顺手挡掉 /login、/register 自指（会被
 * AuthGuard 二次重定向成循环）。
 */
export function buildLoginRedirectFor(loc?: PathLike | null): string {
  const target = getSafePostLoginPath(toFullPath(loc))
  return target ? `/login?from=${encodeURIComponent(target)}` : '/login'
}

/**
 * 校验登录后回跳目标，防开放重定向。
 * 只接受同源相对路径，并排除会被 AuthGuard 二次重定向的 /login、/register。
 */
export function getSafePostLoginPath(path?: string | null): string | null {
  if (!path) return null
  // 必须是站内绝对路径
  if (!path.startsWith('/')) return null
  // 防协议相对 URL（//evil.com）和反斜杠变体（部分浏览器把 \ 归一化为 /）
  if (path.startsWith('//') || path.startsWith('/\\')) return null
  // 排除会再次被 AuthGuard 重定向走的路径，避免循环
  const pathname = path.split('?')[0].split('#')[0]
  if (pathname === '/login' || pathname === '/register') return null
  return path
}
